import { useMemo } from 'react'
import { styled, CircularProgress } from '@mui/material'
import {
  StyledDialog,
  StyledDialogActions,
  StyledDialogContent,
  StyledDialogTitle,
  Title,
} from '..'
import { Button } from '../../../../../components/buttons'
import { intl } from '../../../../../../i18n'
import TextInput from '../../../../../components/editors/input/TextInput'
import { colorPalette } from '../../../../../style/colorPalette'
import { useTemplateDialog } from './hooks/useTemplateDialog'
import { validateTemplateName } from '../../../../../../domain/entity/version-report/VersionReportTemplateEntity/validator'
import { VersionReportTemplateEntity } from '../../../../../../domain/entity/version-report/VersionReportTemplateEntity'

type TemplateDialogProps = {
  open: boolean
  onAfterSubmit: () => void | Promise<void>
  onClose: () => void
  original?: VersionReportTemplateEntity
}

export const TemplateDialog = ({
  open,
  onAfterSubmit,
  onClose,
  original,
}: TemplateDialogProps) => {
  const { templateName, setTemplateName, loading, handleSubmit, handleClose } =
    useTemplateDialog({ onAfterSubmit, onClose, original })

  const templateNameErrors = useMemo(
    () => validateTemplateName(templateName),
    [templateName]
  )
  const hasError = templateNameErrors.length > 0

  return (
    <StyledDialog open={open} onClose={handleClose}>
      <StyledDialogTitle>
        <Title>{original ? 'フォルダ名の編集' : 'フォルダの新規作成'}</Title>
      </StyledDialogTitle>
      <StyledDialogContent>
        <TemplateNameInput
          label="フォルダ名"
          value={templateName}
          onChange={e => setTemplateName(e.target.value)}
          required={true}
          disabled={loading}
          variant="outlined"
          error={hasError}
          helperText={templateNameErrors[0] || ''}
        />
      </StyledDialogContent>
      <StyledDialogActions>
        <SubmitButtonContainer>
          <Button
            onClick={handleSubmit}
            disabled={loading || hasError || !templateName.trim()}
            colorPattern="skyBlue"
          >
            {loading
              ? '保存中...'
              : original
                ? 'フォルダ名を更新する'
                : 'フォルダを保存する'}
          </Button>
          {loading && <LoadingSpinner size={20} />}
        </SubmitButtonContainer>
        <Button
          onClick={handleClose}
          colorPattern="monotone"
          variant="outlined"
          disabled={loading}
        >
          {intl.formatMessage({ id: 'dialog.cancel' })}
        </Button>
      </StyledDialogActions>
    </StyledDialog>
  )
}

const TemplateNameInput = styled(TextInput)({
  width: '360px',
})

const SubmitButtonContainer = styled('div')({
  position: 'relative',
  display: 'inline-block',
})

const LoadingSpinner = styled(CircularProgress)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  marginTop: '-10px',
  marginLeft: '-10px',
  color: colorPalette.monotone[0],
})
